import searchFactory from './searchFactory';
import { track } from '../tracking/track';
import 'element-closest-polyfill';

const SEARCH = 'mc-search';
const SEARCH_FORM = `${SEARCH}-form`;
const SEARCH_RESULTS = `${SEARCH}-results`;
const SEARCH_RESULTS_OPEN = `${SEARCH_RESULTS}--open`;
const SEARCH_LINK = 'mc-nav-item__content';

const _searchComponentInstances = [];

function withCountryPrefix(href) {
  href = href.replace(/^https?:\/\/[^/]+/, '');
  const prefixMatch = window.location.pathname.match(/^(?:\/\w{2})+\b/);
  return prefixMatch ? prefixMatch[0] + href : href;
}

/**
 * Handles inner DOM workings of the .mc-search component.
 * For the fetch functionality, see searchAjax.js
 */
export default class SearchComponent {
  /**
   * Gets or creates the instance of the SearchComponent associated with the given DOM element.
   * @param {Element} el - The DOM element, e.g. a .mc-search-form or input inside it
   */
  static getInstance(el) {
    const search = el.closest(`.${SEARCH_FORM}`);
    if(!search || !search.dataset) return;

    if(search.dataset.searchIndex) {
      return _searchComponentInstances[parseInt(search.dataset.searchIndex)];
    } else {
      const instance = new SearchComponent(search);
      _searchComponentInstances.push(instance);
      search.dataset.searchIndex = _searchComponentInstances.length - 1;
      return instance;
    }
  }

  constructor(el) {
    this.$form = el.closest(`.${SEARCH_FORM}`);
    this.$results = this.$form.querySelector(`.${SEARCH_RESULTS}`);
    this.cachedResults = {};
    this.searcher = searchFactory({
      engine: this.$form.dataset.engine,
      filters: JSON.parse(this.$form.dataset.filters || '{}')
    });
  }

  i18n(key) {
    return JSON.parse(this.$form.dataset.i18n)[key];
  }

  open() {
    this.$results.classList.add(SEARCH_RESULTS_OPEN);
  }

  close() {
    this.$results.classList.remove(SEARCH_RESULTS_OPEN);
  }

  displayHtml(html) {
    this.$results.innerHTML = `<ul class="mc-nav">${html}</ul>`;
    this.open();
  }

  displayStatusMessage(text) {
    this.displayHtml(
      `<li class="mc-nav-item mc-nav-item--fetch-status">
        <span class="mc-nav-item__content">${text}</span>
      </li>`
    );
  }

  displayResults(results) {
    this.displayHtml(results.map((result) => (
      `<li class="mc-nav-item mc-navitem--fetch-result mc-nav-item--${result.type}">
        <a class="mc-nav-item__content" ${result.href && `href="${withCountryPrefix(result.href)}"`} tabindex="-1">
          ${result.text.match(/^i18n/) ? this.i18n(result.text.replace('i18n.', '')) : result.text}
        </a>
      </li>`
    )).join(''));
  }

  handleSearchResults(value, results) {
    if(results.length) {
      this.displayResults(results);
      this.track('search', { label: value });
    } else {
      this.displayStatusMessage(this.i18n('no_results'));
      this.track('searchEmpty', { label: value });
    }
  }

  handleSearchError = (err) => {
    // DOMException is raised if we abort the request, which is totally acceptable
    if(!(err instanceof DOMException)) {
      this.displayStatusMessage(this.i18n('error'));
    }
  };

  clickFirst() {
    const link = this.$results.querySelector(`a.${SEARCH_LINK}[href]`);
    if (link) { link.click(); }
  }

  /**
   * Performs the main search, either from cached results or from Ajax.
   * @param {String} value - The search string
   * @returns {Promise<Array>} - The results of the search
   */
  async search(value) {
    if(value) {
      if(this.cachedResults[value]) {
        this.handleSearchResults(value, this.cachedResults[value]);
      } else {
        this.displayStatusMessage(this.i18n('searching'));
        return this.ajaxSearch(value);
      }
    }
  }

  /**
   * Performs the Ajax call.
   * @param {String} value - The search string
   * @returns {Promise<Array>} - The results of the search
   */
  ajaxSearch(value) {
    return this.searcher.search(value)
      .then((results) => {
        this.cachedResults[value] = results;
        this.handleSearchResults(value, results);
      })
      .catch(this.handleSearchError);
  }

  /**
   * Tracks an event related to the search component.
   * @param {String} actionName - The action to track
   * @param {Object} opts - Extra options to send about the tracking event
   */
  track(actionName, opts = {}) {
    const target = this.$form.tagName;
    const className = this.$form.className;
    const category = 'atlas';

    track(actionName, {
      className, target, category,
      ...opts
    });
  }

}
